import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { Calculator, ArrowRight, TrendingUp, MapPin, DollarSign, BarChart3, ChevronRight, ExternalLink, Shield, Users, Award, Clock, Search, ArrowLeftRight, Sparkles, Home } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { setCanonical, setPageTitle, setMetaDescription, generateWebsiteSchema, generateOrganizationSchema, generateFAQSchema, generateServiceSchema, generateHowToSchema, generateSpeakableSchema } from "@/utils/seo";
import { calculateBreakdown, formatMoney } from "@/utils/taxCalculator";
import { BottomAdBanner, SidebarAds } from "@/components/SponsorBanners";
import FinancialDisclaimer from "@/components/FinancialDisclaimer";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HERO_IMG = "https://images.pexels.com/photos/285011/pexels-photo-285011.jpeg?auto=compress&cs=tinysrgb&w=1400";

const QUICK_STARTS = [
  { label: "$60K in Houston", salary: 60, city: "houston" },
  { label: "$80K in Denver", salary: 80, city: "denver" },
  { label: "$100K in NYC", salary: 100, city: "new-york" },
  { label: "$120K in Seattle", salary: 120, city: "seattle" },
  { label: "$80K in Austin", salary: 80, city: "austin" },
  { label: "$150K in SF", salary: 150, city: "san-francisco" },
];

export default function HomePage() {
  const [cities, setCities] = useState([]);
  const [salary, setSalary] = useState("");
  const [selectedCity, setSelectedCity] = useState("");
  const [result, setResult] = useState(null);
  const [sessionCount, setSessionCount] = useState(1247);
  const [blogs, setBlogs] = useState([]);
  const [tip, setTip] = useState("");
  const [citySearch, setCitySearch] = useState("");

  useEffect(() => {
    setCanonical("/");
    setPageTitle("Cost of Living & Salary Calculator for US Cities");
    setMetaDescription("Compare cost of living, calculate salary after taxes, and find out if you can afford to live in any US city. Free interactive tools for 1,000+ pages.");
    axios.get(`${API}/cities`).then(r => setCities(r.data)).catch(() => {});
    axios.post(`${API}/session-count`).then(r => setSessionCount(r.data.count)).catch(() => {});
    axios.get(`${API}/blogs`).then(r => setBlogs(r.data.slice(0, 3))).catch(() => {});
    axios.get(`${API}/tip-of-the-week`).then(r => setTip(r.data.tip)).catch(() => {});
  }, []);

  const handleCalculate = () => {
    if (!salary || !selectedCity) return;
    const city = cities.find(c => c.slug === selectedCity);
    if (city) setResult(calculateBreakdown(parseFloat(salary), city));
  };

  const filteredCities = useMemo(() => {
    if (!citySearch.trim()) return cities;
    return cities.filter(c => c.name.toLowerCase().includes(citySearch.toLowerCase()) || c.state_code.toLowerCase().includes(citySearch.toLowerCase()));
  }, [cities, citySearch]);

  const verdictClass = result?.verdict === "Comfortable" ? "verdict-comfortable" : result?.verdict === "Tight" ? "verdict-tight" : "verdict-not-affordable";

  const faqs = [
    { question: "How is the cost of living calculated?", answer: "We use real data including median rent, groceries, transportation, utilities, and healthcare costs for each city, combined with 2025 federal and state tax calculations." },
    { question: "Are the tax calculations accurate?", answer: "We use 2025 federal tax brackets, FICA rates, and state income tax rates. Results are estimates - consult a tax professional for exact figures." },
    { question: "What does the 'Comfortable' verdict mean?", answer: "Comfortable means you'll have more than 20% of your take-home pay remaining after essential expenses. 'Tight' means you can cover expenses but savings will be limited." },
    { question: "How many cities and pages do you have?", answer: "We cover 20+ major US cities with 1,000+ individual pages including salary breakdowns, affordability checks, and city comparisons. Expanding to 100+ cities." },
    { question: "Is this financial advice?", answer: "No. AffordScope provides estimates for informational purposes only. Always consult qualified financial professionals for personalized advice." },
  ];

  return (
    <div data-testid="home-page">
      <script type="application/ld+json">{JSON.stringify(generateWebsiteSchema())}</script>
      <script type="application/ld+json">{JSON.stringify(generateOrganizationSchema())}</script>
      <script type="application/ld+json">{JSON.stringify(generateFAQSchema(faqs))}</script>
      <script type="application/ld+json">{JSON.stringify(generateServiceSchema())}</script>
      <script type="application/ld+json">{JSON.stringify(generateHowToSchema())}</script>
      <script type="application/ld+json">{JSON.stringify(generateSpeakableSchema("/", ["Can You Afford to Live There?"], ["Free salary and cost of living calculator for US cities"]))}</script>

      {/* ===== HERO ===== */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 z-0">
          <img src="https://customer-assets.emergentagent.com/job_affordscope-preview/artifacts/7i9w60dt_I%20see%202.png" alt="" className="w-full h-full object-cover hero-zoom-bg" />
        </div>

        <div className="relative z-10 py-10 md:py-14">
          <div className="container-main">
            <div className="max-w-3xl mx-auto text-center mb-8">
              <div className="flex items-center justify-center gap-4 mb-5">
                <div className="flex items-center gap-1.5 text-xs text-slate-500">
                  <Users className="w-3.5 h-3.5 text-emerald-600" />
                  <span className="font-mono font-semibold text-slate-700">{sessionCount.toLocaleString()}</span> calculations
                </div>
                <div className="w-px h-4 bg-slate-300"></div>
                <div className="flex items-center gap-1.5 text-xs text-slate-500">
                  <MapPin className="w-3.5 h-3.5 text-emerald-600" />
                  <span className="font-semibold text-slate-700">20+</span> cities
                </div>
                <div className="w-px h-4 bg-slate-300"></div>
                <div className="flex items-center gap-1.5 text-xs text-slate-500">
                  <Shield className="w-3.5 h-3.5 text-emerald-600" />
                  <span className="font-semibold text-slate-700">100%</span> free
                </div>
              </div>
              <h1 className="speakable-headline text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-800 mb-4 leading-tight">
                Can You Afford<br />to Live There?
              </h1>
              <p className="speakable-summary text-base md:text-lg text-slate-700 max-w-xl mx-auto font-medium" style={{ textShadow: '0 1px 8px rgba(255,255,255,0.9), 0 0 20px rgba(255,255,255,0.8)' }}>
                Real salary data. Real tax calculations. Real cost of living. Find out in seconds if your dream city fits your budget.
              </p>
            </div>
            <Card className="max-w-2xl mx-auto shadow-xl border-slate-200/80 bg-white/95 backdrop-blur-sm" data-testid="salary-calculator">
              <CardContent className="p-6 md:p-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 block">Annual Salary</label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <input type="number" value={salary} onChange={e => setSalary(e.target.value)} placeholder="Your Salary" data-testid="salary-input"
                        className="w-full h-14 pl-10 pr-4 text-lg border border-slate-200 rounded-lg focus:border-emerald-600 focus:ring-4 focus:ring-emerald-600/10 bg-white shadow-sm transition-all outline-none font-mono" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 block">City</label>
                    <Select value={selectedCity} onValueChange={setSelectedCity}>
                      <SelectTrigger className="h-14 text-lg border-slate-200 shadow-sm" data-testid="city-select-trigger">
                        <SelectValue placeholder="Select a city" />
                      </SelectTrigger>
                      <SelectContent>
                        {cities.map(c => <SelectItem key={c.slug} value={c.slug} data-testid={`city-option-${c.slug}`}>{c.name}, {c.state_code}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Button onClick={handleCalculate} className="w-full h-12 text-base bg-emerald-700 hover:bg-emerald-800 shadow-lg shadow-emerald-700/20"
                  disabled={!salary || !selectedCity} data-testid="calculate-button">
                  <Calculator className="w-5 h-5 mr-2" /> Calculate Affordability
                </Button>
              </CardContent>
            </Card>
            <div className="max-w-2xl mx-auto mt-4 text-center" data-testid="quick-starts">
              <p className="text-xs text-slate-400 mb-2">Popular searches:</p>
              <div className="flex flex-wrap justify-center gap-2">
                {QUICK_STARTS.map(qs => (
                  <Link key={qs.label} to={`/${qs.salary}k-salary-in-${qs.city}`}
                    className="text-xs px-3 py-1.5 rounded-full border border-slate-200 bg-white/80 text-slate-600 hover:border-emerald-300 hover:text-emerald-700 hover:bg-emerald-50 transition-colors no-underline">
                    {qs.label}
                  </Link>
                ))}
              </div>
            </div>
            {result && (
              <div className="max-w-2xl mx-auto mt-8 fade-in-up" data-testid="calculation-result">
                <Card className="shadow-xl border-slate-200">
                  <CardContent className="p-6 md:p-8">
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <p className="text-sm text-slate-500">Living in {result.city_name}, {result.state_code}</p>
                        <p className="text-2xl font-bold text-slate-900 font-mono">{formatMoney(result.gross_salary)}/yr</p>
                      </div>
                      <span className={`px-4 py-2 rounded-full text-sm font-bold ${verdictClass}`} data-testid="verdict-badge">{result.verdict}</span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                      {[
                        { label: "Monthly Take-Home", value: formatMoney(result.net_monthly), color: "text-emerald-700" },
                        { label: "Monthly Expenses", value: formatMoney(result.total_monthly_expenses), color: "text-slate-700" },
                        { label: "Remaining", value: formatMoney(result.remaining_monthly), color: result.remaining_monthly >= 0 ? "text-emerald-700" : "text-red-600" },
                        { label: "Rent-to-Income", value: `${result.rent_to_income_ratio}%`, color: result.rent_to_income_ratio <= 30 ? "text-emerald-700" : "text-amber-600" },
                      ].map((s, i) => (
                        <div key={i} className="bg-slate-50 rounded-xl p-3 text-center">
                          <p className="text-xs text-slate-500 mb-1">{s.label}</p>
                          <p className={`text-lg font-bold font-mono ${s.color}`}>{s.value}</p>
                        </div>
                      ))}
                    </div>
                    {[
                      { label: "Rent (1BR)", value: result.rent_1br, max: result.net_monthly },
                      { label: "Groceries", value: result.groceries, max: result.net_monthly },
                      { label: "Transportation", value: result.transportation, max: result.net_monthly },
                      { label: "Utilities", value: result.utilities, max: result.net_monthly },
                      { label: "Healthcare", value: result.healthcare, max: result.net_monthly },
                    ].map((e, i) => (
                      <div key={i} className="mb-2">
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-slate-600">{e.label}</span>
                          <span className="font-mono text-slate-700">{formatMoney(e.value)}</span>
                        </div>
                        <Progress value={Math.min((e.value / e.max) * 100, 100)} className="h-2" />
                      </div>
                    ))}
                    <div className="mt-4 pt-4 border-t border-slate-100 flex gap-3">
                      <Link to={`/cost-of-living-in-${selectedCity}`}><Button variant="outline" size="sm">Full City Details <ChevronRight className="w-4 h-4 ml-1" /></Button></Link>
                      <Link to={`/${Math.round(result.gross_salary/1000)}k-salary-in-${selectedCity}`}><Button variant="outline" size="sm">Full Breakdown <ChevronRight className="w-4 h-4 ml-1" /></Button></Link>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ===== Tip Bar ===== */}
      {tip && (
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center gap-3 px-5 py-3 bg-emerald-50 border border-emerald-200 rounded-xl">
            <TrendingUp className="w-5 h-5 text-emerald-600 flex-shrink-0" />
            <p className="text-xs sm:text-sm text-emerald-800 leading-relaxed"><strong>Tip of the Week:</strong> {tip}</p>
          </div>
        </div>
      )}

      {/* Sponsored Partners — Apartment Featured */}
      <section className="py-6 bg-white border-b border-slate-100" data-testid="sponsor-section">
        <div className="container-main">
          <p className="text-[10px] text-slate-400 uppercase tracking-widest text-center mb-3">Sponsored Partners</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 max-w-4xl mx-auto">
            {/* APARTMENT — Main featured ad (spans 2 cols) */}
            <a href="https://www.hellolanding.com/r/web/2vvg1H" target="_blank" rel="noopener" className="no-underline md:col-span-2" data-testid="sponsor-apartment-main">
              <div className="flex items-center gap-4 p-4 rounded-xl border-2 border-blue-200 bg-gradient-to-r from-blue-50 via-sky-50 to-blue-50 hover:shadow-lg transition-all h-full">
                <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0">
                  <img src="https://images.pexels.com/photos/19899060/pexels-photo-19899060.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop" alt="Furnished apartment" className="w-full h-full object-cover" />
                </div>
                <div className="flex-1">
                  <p className="text-base font-bold text-slate-900">Moving? Get $250 Off Your First Furnished Apartment</p>
                  <p className="text-sm text-slate-500">Move freely, stay comfortably. Furnished apartments in 200+ cities. *Limited time offer. Terms apply.</p>
                </div>
                <div className="flex-shrink-0 hidden md:block">
                  <div className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors">
                    Claim $250 Off
                  </div>
                </div>
              </div>
            </a>
            {/* Budget Planner */}
            <Link to="/budget-planner" className="no-underline">
              <div className="flex items-center gap-3 p-4 rounded-xl border border-amber-100 bg-gradient-to-r from-amber-50 to-white hover:shadow-md transition-all h-full">
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <Calculator className="w-5 h-5 text-amber-700" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-900">Free Budget Planner</p>
                  <p className="text-xs text-slate-500 truncate">AI-powered budget plan + print</p>
                </div>
                <ExternalLink className="w-4 h-4 text-amber-500 flex-shrink-0" />
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* ===== MAIN CONTENT + SIDEBAR LAYOUT ===== */}
      <div className="container-main py-12">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-8">
          {/* Main Column */}
          <div>
            {/* Compare Cities — MOVED UP */}
            <section className="mb-10 p-6 bg-primary rounded-2xl" data-testid="compare-section">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                    <ArrowLeftRight className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">Compare Two Cities</h2>
                    <p className="text-sm text-emerald-200">See which city gives you more bang for your buck.</p>
                  </div>
                </div>
                <Link to="/compare">
                  <Button size="lg" className="bg-white text-primary hover:bg-white/90 font-semibold shadow-lg" data-testid="cta-compare-button">
                    Compare Now <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </section>

            {/* City Spotlight — Auto-rotates weekly */}
            {cities.length > 0 && (() => {
              const weekNum = Math.floor(Date.now() / (7 * 24 * 60 * 60 * 1000));
              const spotlightCity = cities[weekNum % cities.length];
              if (!spotlightCity) return null;
              const totalMonthly = (spotlightCity.rent_1br || 0) + (spotlightCity.groceries || 0) + (spotlightCity.transportation || 0) + (spotlightCity.utilities || 0) + (spotlightCity.healthcare || 0);
              return (
                <section className="mb-10" data-testid="city-spotlight">
                  <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
                    <div className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <Sparkles className="w-4 h-4 text-amber-500" />
                          <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-xs font-semibold">City Spotlight</Badge>
                        </div>
                        <Link to={`/cost-of-living-in-${spotlightCity.slug}`} className="no-underline">
                          <Button variant="outline" size="sm">Explore {spotlightCity.name} <ArrowRight className="w-3.5 h-3.5 ml-1" /></Button>
                        </Link>
                      </div>
                      <h3 className="text-lg font-bold text-slate-900 mb-1">This Week: {spotlightCity.name}, {spotlightCity.state_code}</h3>
                      <p className="text-sm text-slate-500 mb-4">
                        {spotlightCity.name} has a {spotlightCity.col_index <= 95 ? 'low' : spotlightCity.col_index <= 105 ? 'moderate' : 'high'} cost of living
                        {spotlightCity.state_tax_rate === 0 ? ' and no state income tax' : ''}, making it {spotlightCity.col_index <= 100 ? 'more affordable than' : 'pricier than'} the national average.
                      </p>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                        <div className="bg-slate-50 rounded-xl p-3 text-center">
                          <p className="text-lg font-bold font-mono text-slate-800">{formatMoney(spotlightCity.median_household_income)}</p>
                          <p className="text-[10px] text-slate-500">Median Income</p>
                        </div>
                        <div className="bg-slate-50 rounded-xl p-3 text-center">
                          <p className="text-lg font-bold font-mono text-slate-800">{formatMoney(spotlightCity.rent_1br)}</p>
                          <p className="text-[10px] text-slate-500">Avg Rent (1BR)</p>
                        </div>
                        <div className="bg-slate-50 rounded-xl p-3 text-center">
                          <p className={`text-lg font-bold font-mono ${spotlightCity.col_index <= 100 ? 'text-emerald-700' : 'text-amber-700'}`}>{spotlightCity.col_index}</p>
                          <p className="text-[10px] text-slate-500">COL Index</p>
                        </div>
                        <div className="bg-slate-50 rounded-xl p-3 text-center">
                          <p className="text-lg font-bold font-mono text-slate-800">{formatMoney(totalMonthly)}</p>
                          <p className="text-[10px] text-slate-500">Monthly Expenses</p>
                        </div>
                      </div>
                      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                        <p className="text-xs text-amber-800">
                          <strong>Why {spotlightCity.name} Matters:</strong> {spotlightCity.col_index <= 100
                            ? `With a cost of living index of ${spotlightCity.col_index} and rent at ${formatMoney(spotlightCity.rent_1br)}/month, ${spotlightCity.name} is one of the more affordable major cities. A $70K salary here goes further than in most metros.`
                            : `With a cost of living index of ${spotlightCity.col_index} and rent at ${formatMoney(spotlightCity.rent_1br)}/month, ${spotlightCity.name} requires careful budgeting. Use our calculator to see what salary you'd need to live comfortably.`
                          }
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2 mt-4">
                        {[60, 80, 100].map(sal => (
                          <Link key={sal} to={`/${sal}k-salary-in-${spotlightCity.slug}`} className="no-underline">
                            <Badge variant="secondary" className="cursor-pointer hover:bg-emerald-50 hover:text-emerald-700 hover:border-emerald-200 transition-colors">
                              ${sal}K in {spotlightCity.name}
                            </Badge>
                          </Link>
                        ))}
                        <Link to="/search-city" className="no-underline">
                          <Badge variant="secondary" className="cursor-pointer hover:bg-emerald-50 hover:text-emerald-700 hover:border-emerald-200 transition-colors">
                            <Search className="w-3 h-3 mr-1" /> Can't find your city?
                          </Badge>
                        </Link>
                      </div>
                    </div>
                  </div>
                </section>
              );
            })()}

            {/* Cities with Search */}
            <section data-testid="popular-cities-section">
              <div className="flex flex-col md:flex-row md:items-end justify-between gap-3 mb-4">
                <div>
                  <h2 className="text-2xl font-bold text-slate-900">Explore Cities</h2>
                  <p className="text-sm text-slate-500 mt-0.5">Cost of living data for 50 US cities</p>
                </div>
                <Link to="/search-city" className="no-underline" data-testid="cant-find-city-link">
                  <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 border-emerald-200 bg-emerald-50 hover:bg-emerald-100 hover:border-emerald-300 transition-all">
                    <Search className="w-4 h-4 text-emerald-700" />
                    <span className="text-sm font-semibold text-emerald-700">Can't Find Your City? Search Here</span>
                  </div>
                </Link>
              </div>
              <div className="relative mb-4" data-testid="city-search">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input type="text" value={citySearch} onChange={e => setCitySearch(e.target.value)}
                  placeholder="Search cities..." data-testid="city-search-input"
                  className="w-full pl-10 pr-4 py-2.5 text-sm border border-slate-200 rounded-lg focus:border-emerald-600 focus:ring-2 focus:ring-emerald-600/10 outline-none" />
                {citySearch && filteredCities.length === 0 && (
                  <div className="mt-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <p className="text-xs text-emerald-700">Can't find "{citySearch}"? <Link to="/search-city" className="font-semibold underline">Generate a report for any US city</Link></p>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {filteredCities.map(city => (
                  <Link key={city.slug} to={`/cost-of-living-in-${city.slug}`} className="no-underline" data-testid={`city-card-${city.slug}`}>
                    <div className="card-hover p-3.5 rounded-xl border border-slate-100 bg-white hover:border-emerald-200">
                      <div className="flex items-start justify-between mb-1.5">
                        <h3 className="font-semibold text-slate-900 text-sm leading-tight">{city.name}</h3>
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-md ${
                          city.col_index <= 100 ? 'bg-emerald-100 text-emerald-700' : city.col_index <= 130 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                        }`}>{city.col_index}</span>
                      </div>
                      <p className="text-[10px] text-slate-400 mb-2">{city.state_code} {city.state_tax_rate === 0 ? '| No state tax' : ''}</p>
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-slate-500">Rent</span>
                        <span className="text-sm font-bold font-mono text-slate-800">{formatMoney(city.rent_1br)}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </section>

            {/* Mobile-Only Ads (hidden on desktop where sidebar shows) */}
            <div className="lg:hidden mt-8 space-y-4" data-testid="mobile-ads">
              <div className="flex justify-center">
                <a href="https://creditcane.com" target="_blank" rel="noopener" className="no-underline block">
                  <div className="flex items-center gap-3 px-5 py-3 bg-gradient-to-r from-sky-50 via-cyan-50 to-sky-100 border-2 border-sky-200 rounded-xl hover:shadow-md transition-all">
                    <DollarSign className="w-4 h-4 text-sky-700 flex-shrink-0" />
                    <p className="text-sm font-semibold text-sky-900">Bad Credit? We Can Help</p>
                    <span className="text-xs font-semibold text-sky-700 whitespace-nowrap">Fix Credit &rarr;</span>
                  </div>
                </a>
              </div>
              <div className="bg-white border border-slate-200 rounded-xl p-4 text-center">
                <p className="text-[10px] text-slate-400 mb-2">Advertisement</p>
                <div className="bg-slate-50 rounded-lg py-10 border border-dashed border-slate-200">
                  <p className="text-xs text-slate-400">Google AdSense</p>
                </div>
              </div>
            </div>

            {/* How It Works + Stats */}
            <section className="mt-10 py-8 bg-slate-50 rounded-2xl px-6" data-testid="how-it-works-section">
              <h2 className="text-lg font-bold text-slate-900 mb-5 text-center">How AffordScope Works</h2>
              <div className="grid grid-cols-3 gap-4">
                {[
                  { icon: DollarSign, title: "Enter Salary", desc: "Your annual gross pay" },
                  { icon: MapPin, title: "Pick a City", desc: "From 20+ US metros" },
                  { icon: BarChart3, title: "Get Verdict", desc: "Instant affordability" },
                ].map((s, i) => (
                  <div key={i} className="text-center">
                    <div className="w-10 h-10 mx-auto mb-2 rounded-xl bg-emerald-100 flex items-center justify-center">
                      <s.icon className="w-5 h-5 text-emerald-700" />
                    </div>
                    <h3 className="text-sm font-semibold text-slate-900">{s.title}</h3>
                    <p className="text-xs text-slate-500">{s.desc}</p>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-4 gap-3 mt-6 pt-6 border-t border-slate-200 text-center">
                {[
                  { value: sessionCount.toLocaleString(), label: "Calculations" },
                  { value: "20+", label: "Cities" },
                  { value: "1,000+", label: "Pages" },
                  { value: "Free", label: "Forever" },
                ].map((s, i) => (
                  <div key={i}>
                    <p className="text-lg font-bold font-mono text-primary">{s.value}</p>
                    <p className="text-[10px] text-slate-500 uppercase tracking-wider">{s.label}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* FAQ */}
            <section className="mt-10" data-testid="faq-section">
              <h2 className="text-xl font-bold text-slate-900 mb-5">Frequently Asked Questions</h2>
              <Accordion type="single" collapsible className="space-y-2">
                {faqs.map((faq, i) => (
                  <AccordionItem key={i} value={`faq-${i}`} className="border border-slate-200 rounded-xl px-4 data-[state=open]:bg-slate-50">
                    <AccordionTrigger className="text-sm font-medium text-slate-800 hover:no-underline py-4" data-testid={`faq-trigger-${i}`}>{faq.question}</AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 pb-4">{faq.answer}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </section>

            {/* Blog */}
            {blogs.length > 0 && (
              <section className="mt-10" data-testid="blog-section">
                <div className="flex items-center justify-between mb-5">
                  <h2 className="text-xl font-bold text-slate-900">Latest Articles</h2>
                  <Link to="/blog"><Button variant="ghost" size="sm">View All <ArrowRight className="w-4 h-4 ml-1" /></Button></Link>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {blogs.map(b => (
                    <Link key={b.slug} to={`/blog/${b.slug}`} className="no-underline">
                      <Card className="card-hover h-full"><CardContent className="p-5">
                        <h3 className="font-semibold text-slate-900 mb-2 line-clamp-2 text-sm">{b.title}</h3>
                        <p className="text-xs text-slate-500 line-clamp-2">{b.meta_description}</p>
                      </CardContent></Card>
                    </Link>
                  ))}
                </div>
              </section>
            )}

            <FinancialDisclaimer />
          </div>

          {/* ===== SIDEBAR — Desktop Only ===== */}
          <div className="hidden lg:block" data-testid="homepage-sidebar">
            <SidebarAds />
          </div>
        </div>
      </div>

      {/* Bottom customizable banner */}
      <BottomAdBanner />
    </div>
  );
}
